import { lock, unlock } from 'tua-body-scroll-lock'
import scrollIntoView from 'scroll-into-view-if-needed'
import { debounce, matchesMediaQuery } from '../utilities'
import {
  ACTIVE_CLASS,
  HIDDEN_CLASS,
  MENU_OPEN_CLASS,
  SEARCH_OPEN_CLASS,
  FOCUSABLE_SELECTORS,
} from '../utilities/constants'

export default class {
  constructor(el, options) {
    const extraScrollOffset = 3
    let lastScrollY = 0
    let isScrollingDown = false
    let isSticky = false
    let scrollHeight = 0

    const observer = new IntersectionObserver(
      ([e]) => {
        isSticky = e.intersectionRatio < 1
      },
      { threshold: [1] },
    )

    function handleScroll() {
      if (window.scrollY + window.innerHeight > scrollHeight) {
        return
      }

      const elHeight = el.offsetHeight

      if (options?.sticky) {
        if (isSticky) {
          if (el.offsetTop + elHeight / 2 < el.nextElementSibling.offsetTop) {
            isScrollingDown = false
            el.classList.remove(HIDDEN_CLASS)
            return
          }
        } else {
          isScrollingDown = false
          el.classList.remove(HIDDEN_CLASS)
          return
        }
      }

      if (document.documentElement.classList.contains(MENU_OPEN_CLASS)) {
        isScrollingDown = false
        el.classList.remove(HIDDEN_CLASS)
        return
      }

      if (window.scrollY <= elHeight / 2) {
        isScrollingDown = false
        el.classList.remove(HIDDEN_CLASS)
        return
      }

      if (lastScrollY) {
        if (
          window.scrollY > lastScrollY + extraScrollOffset &&
          window.scrollY > elHeight &&
          !isScrollingDown
        ) {
          isScrollingDown = true
          el.classList.add(HIDDEN_CLASS)
        } else if (
          window.scrollY < lastScrollY - extraScrollOffset &&
          isScrollingDown
        ) {
          isScrollingDown = false
          el.classList.remove(HIDDEN_CLASS)
        }
      }

      lastScrollY = window.scrollY
    }

    if (options?.hide) {
      window.addEventListener('scroll', handleScroll, {
        passive: true,
      })

      handleScroll()

      observer.observe(el)
    }

    let prevHeight = 0
    function handleResize() {
      scrollHeight = Math.max(
        document?.body?.scrollHeight,
        document?.body?.offsetHeight,
        document?.documentElement?.clientHeight,
        document?.documentElement?.scrollHeight,
        document?.documentElement?.offsetHeight,
      )

      if (prevHeight === scrollHeight) {
        return
      }

      document.documentElement.style.removeProperty('--Head-height')

      window.requestAnimationFrame(() => {
        document.documentElement.style.setProperty(
          '--Head-height',
          `${el.offsetHeight}px`,
        )

        prevHeight = scrollHeight
      })
    }

    window.addEventListener('resize', debounce(handleResize, 200), {
      passive: true,
    })

    handleResize()

    /* Navigation */
    const navToggleEls = Array.from(el.querySelectorAll('.js-headNavButton'))

    let navTimer

    function toggleNav(forceState = null) {
      clearTimeout(navTimer)

      const isActive =
        forceState ?? document.documentElement.classList.toggle(MENU_OPEN_CLASS)

      const navigationContainer = document.body.querySelector(
        '.js-navigationContainer',
      )

      if (isActive) {
        el.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        })

        lock(navigationContainer)

        const firstFocusable =
          navigationContainer.querySelector(FOCUSABLE_SELECTORS)

        firstFocusable.focus({
          focusVisible: false,
        })
      } else {
        unlock(navigationContainer)
      }
    }

    if (navToggleEls) {
      navToggleEls.forEach((navToggleEl) =>
        navToggleEl.addEventListener(
          'click',
          function (e) {
            e.preventDefault()

            toggleNav()
          },
          false,
        ),
      )
    }

    /* Search form */
    const searchToggleEls = Array.from(
      el.querySelectorAll('.js-headSearchButton'),
    )
    const searchEl = el.querySelector('.js-headSearch')
    const searchInputEl = searchEl.querySelector('.js-headSearchInput')

    function handleToggleSearch() {
      const isActive =
        document.documentElement.classList.toggle(SEARCH_OPEN_CLASS)

      if (isActive) {
        if (matchesMediaQuery('(max-width: 899px)')) {
          searchInputEl.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          })
        }

        searchInputEl.focus({
          preventScroll: true,
        })
      } else {
        searchInputEl.value = ''
      }
    }

    function handleSearchBlur(e) {
      if (searchInputEl.value) {
        return
      }

      const currentTarget = e.currentTarget

      window.requestAnimationFrame(() => {
        if (!currentTarget.contains(document.activeElement)) {
          document.documentElement.classList.remove(SEARCH_OPEN_CLASS)
        }
      })
    }

    if (searchToggleEls && searchEl) {
      searchEl.addEventListener('blur', handleSearchBlur, true)

      searchToggleEls.forEach((searchToggleEl) => {
        searchToggleEl.addEventListener('click', handleToggleSearch, false)
      })

      window.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          toggleNav(false)
          document.documentElement.classList.remove(SEARCH_OPEN_CLASS)
        }
      })
    }

    if (searchInputEl) {
      window.addEventListener(
        'hashchange',
        () => {
          if (location.hash === `#${searchInputEl.id}`) {
            document.documentElement.classList.add(SEARCH_OPEN_CLASS)

            searchInputEl.focus({
              preventScroll: true,
            })
          }
        },
        false,
      )
    }

    /* Second navigation */
    const secondNavEl = document.body.querySelector('.js-headSecondNav')

    function updateSecondNavPosition() {
      if (secondNavEl) {
        secondNavEl.parentElement.style.setProperty(
          '--Head-second-scroll',
          `${Math.min(1, secondNavEl.scrollLeft / 2)}`,
        )
      }
    }

    if (secondNavEl) {
      const activeEl = secondNavEl.querySelector(`.${ACTIVE_CLASS}`)

      if (activeEl) {
        scrollIntoView(activeEl, {
          behavior: 'auto',
          block: 'nearest',
          boundary: secondNavEl,
          inline: 'center',
          scrollMode: 'if-needed',
        })

        updateSecondNavPosition()
      } else {
        scrollIntoView(secondNavEl.firstElementChild, {
          behavior: 'auto',
          block: 'nearest',
          boundary: secondNavEl,
          inline: 'start',
          scrollMode: 'if-needed',
        })
      }

      secondNavEl.addEventListener('scroll', updateSecondNavPosition, {
        passive: true,
      })
    }
  }
}
